import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {SettingsPageContainer} from '../../../../../components/Settings/SettingsPageContainer';
import {PasswordContainer} from '../../../../../components/PasswordContainer';
import {useRPC} from '../../../../../hooks/rpc';
import {WifiNetworkModel} from '../../../../../types/wifi-network';
import {ColumnModel, SettingsList} from '../../../../../components/Settings/SettingsList';
import {useMessage} from '../../../../../hooks/message';
import {useParams} from 'react-router-dom';
import iconSignal0 from '../../../../../assets/images/icons/settings/signal0.png';
import iconSignal1 from '../../../../../assets/images/icons/settings/signal1.png';
import iconSignal2 from '../../../../../assets/images/icons/settings/signal2.png';
import iconSignal3 from '../../../../../assets/images/icons/settings/signal3.png';
import {useSetSelector} from '../../../../../hooks/set-selector';
import {useFilterSelector} from '../../../../../hooks/filter-selector';
import {useSelector} from 'react-redux';
import {SettingInputComponent} from '../../../../../components/Settings/SettingInputComponent';
import {SettingReadOnlyRowComponent} from '../../../../../components/Settings/SettingReadOnlyRowComponent';

const BACK_URL = '/settings/gateway';

const columns: ColumnModel[] = [
    {
        length: 3,
        name: 'WiFi Adı',
        key: 'name',
    },
    {
        length: 2,
        name: 'Kanal',
        key: 'channel',
    },
    {
        length: 2,
        name: 'Sinyal Gücü',
        key: 'signal',
    },
    {
        length: 3,
        name: 'Güvenlik',
        key: 'security',
    },
];

const keyExtractor = (item: WifiNetworkModel) => item.name;

export const WiFiListScreen = memo(function WiFiListScreen() {
    const params = useParams<{ id: string }>();
    const {success, failure} = useMessage();
    const [rpc] = useRPC();

    const [wifiNetworkList, setWiFiNetworkList] = useState<WifiNetworkModel[]>([]);
    const [selectedWiFiNetwork, setSelectedWiFiNetwork] = useState<WifiNetworkModel>(null);

    const currentGatewayId = params.id;
    const selectedGateway = useSelector(state => state.control.gatewayList.find(g => g.id === currentGatewayId));
    const hasWifi = !(selectedGateway?.settings.home_network.is_ethernet && selectedGateway?.settings.remote_network.is_ethernet);

    const gatewayName = selectedGateway?.name;

    const refreshNetworkList = useCallback(() => {
        if (rpc == null) {
            // prevent exception if page is refreshed
            return;
        }

        // setWiFiNetworkList([{name:'sa', signal: 50, channel: '1', security:'1'}]);
        rpc.getWifiNetworkList(currentGatewayId)
            .then(networkList => {
                setWiFiNetworkList(networkList);
            })
            .catch(failure);
    }, [rpc, currentGatewayId, failure]);

    const onScanClick = useCallback(() => {
        setSelectedWiFiNetwork(null);
        setWiFiNetworkList([]);
        refreshNetworkList();
    }, [refreshNetworkList]);

    const onDisconnectPress = useCallback(() => {
        rpc.disconnectWifiNetwork(currentGatewayId)
            .then(() => {
                success('Bağlantı kesildi.');
            }).catch(failure);
    }, [failure, rpc, success, currentGatewayId]);

    const buttons = useMemo(() => [
        {
            text: 'BAĞLANTIYI KES',
            className: 'red',
            onClick: onDisconnectPress,
            hidden: !(selectedGateway?.settings?.wifi_settings?.ssid !== '') || !hasWifi,
        },
        {
            text: 'BAĞLAN',
            url: `/settings/gateway/${currentGatewayId != null ? currentGatewayId : ''}/wifi/${selectedWiFiNetwork != null ? selectedWiFiNetwork.name : ''}`,
            className: 'blue',
            hidden: !(selectedWiFiNetwork) || !hasWifi,
        },
        {
            text: 'MANUEL BAĞLAN',
            url: `/settings/gateway/${currentGatewayId != null ? currentGatewayId : ''}/wifi/connect-manuel`,
            className: 'blue',
            hidden: !hasWifi,
        },
        {
            text: 'TARA',
            onClick: onScanClick,
            className: 'green',
            hidden: !hasWifi,
        },
    ], [currentGatewayId, selectedWiFiNetwork, onScanClick, selectedGateway, onDisconnectPress, hasWifi]);

    const displayNameExtractor = useCallback((item: WifiNetworkModel, column: ColumnModel) => {
        if (column.key === 'signal') {
            const icon = item.signal > 66 ? iconSignal3 :
                item.signal > 33 ? iconSignal2 :
                    item.signal > 0 ? iconSignal1 :
                        iconSignal0;
            return <img src={icon} alt=""/>;
        }

        return item[column.key] || '-';
    }, []);

    useEffect(() => {
        if (currentGatewayId == null) {
            return;
        }

        refreshNetworkList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PasswordContainer requiredRole="admin" isFullscreen={true}>
            <SettingsPageContainer
                className={hasWifi ? undefined : 'service-edit'}
                buttons={buttons}
                backUrl={BACK_URL}
                title={gatewayName || ''}>
                {
                    renderIf(hasWifi)(() => (
                        <SettingsList
                            columns={columns}
                            items={wifiNetworkList}
                            displayNameExtractor={displayNameExtractor}
                            keyExtractor={keyExtractor}
                            selectedItem={selectedWiFiNetwork}
                            onItemSelected={setSelectedWiFiNetwork}/>
                    ), () => (
                        <div className="content-divider">
                            <div className="content-wrapper">
                                <SettingReadOnlyRowComponent
                                    title="Bu cihazda wifi kullanılmamaktadır."
                                    value=" "/>
                            </div>
                        </div>
                    ))
                }
            </SettingsPageContainer>
        </PasswordContainer>
    );
});
